import React, { useContext, useEffect, useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { useParams } from 'react-router-dom';
import { useSnackbar } from 'material-ui-snackbar-provider';
import { Container, Stack, styled } from '@mui/system';
import {
  CircularProgress, Grid, Typography, useMediaQuery,
} from '@mui/material';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import JsxParser from 'react-jsx-parser';
import { useTheme } from '@emotion/react';
import { getInviteFormByPathGql, getInviteGql } from '../InviteResponse/gql';
import RsvpHeading from '../../Components/RsvpHeading';
import { GeneralContext } from '../../util/Store';
import GlobalStyles from '../../Components/GlobalStyles';
import FormContainer from '../../Components/FormContainer';
import { ThemeContext } from '../../util/ThemeStore';
import ExternalLink from '../../Components/ExternalLink';

const ErrorTypo = styled(Typography)({
  textAlign: 'center',
  marginTop: '35vh',
});

export default function Declined() {
  const { inviteCode, path } = useParams();
  const snackbar = useSnackbar();
  const theme = useTheme();
  const generalStore = useContext(GeneralContext);
  const themeStore = useContext(ThemeContext);
  const matchesSm = useMediaQuery(theme.breakpoints.down('sm'));
  const {
    data,
    loading,
  } = useQuery(getInviteGql, {
    fetchPolicy: 'no-cache',
    variables: {
      inviteCode,
    },
    onError: () => snackbar.showMessage('Fehler beim laden.'),
  });

  const { data: inviteFormResult, loading: formLoading } = useQuery(getInviteFormByPathGql, {
    fetchPolicy: 'no-cache',
    variables: { path },
    onCompleted: (result) => {
      if (result?.getInviteFormByPath?.themeConfig?.typography) {
        themeStore.setTypography(result?.getInviteFormByPath?.themeConfig?.typography);
      }
    },
  });

  useEffect(() => {
    if (!data?.getInvite) return;
    generalStore.setFooterLinks(data?.getInvite.inviteForm?.frontendSettings?.footerLinks);
    generalStore.setContactInfo(data?.getInvite.inviteForm.frontendSettings.contactInfo);
  }, [data?.getInvite]);

  const invite = useMemo(() => data?.getInvite, [data?.getInvite]);
  const inviteForm = useMemo(() => inviteFormResult?.getInviteFormByPath?.inviteForm, [inviteFormResult?.getInviteFormByPath?.inviteForm]);

  if (!invite || !inviteForm || loading || formLoading) {
    return (
      <Stack alignItems="center" sx={{ mt: 5 }}>
        <CircularProgress />
      </Stack>
    );
  }

  if (!inviteCode) return (<ErrorTypo variant="h2">Fehlerhafter Link</ErrorTypo>);
  return (
    <Container maxWidth="lg" sx={{ paddingX: '8px' }}>
      <GlobalStyles
        colors={inviteFormResult?.getInviteFormByPath?.colors}
        customCss={inviteFormResult?.getInviteFormByPath?.customCss}
        font={inviteFormResult?.getInviteFormByPath?.font}
      />
      <RsvpHeading logoUrl={inviteFormResult?.getInviteFormByPath?.logoUrl} inviteForm={inviteForm} />
      <FormContainer sx={{ pb: 8, mb: 1 }} elevation={4} matchesSm={matchesSm}>
        <Grid container direction="row" justifyContent="center" sx={{ mb: 2 }}>
          <HighlightOffIcon color="error" sx={{ fontSize: 80 }} />
        </Grid>
        <Typography sx={{ textAlign: 'center', mb: 2 }} variant="h4">
          {inviteForm?.frontendSettings?.declineHeadline
            || inviteForm?.frontendSettings?.declinedHeadline
            || 'Ihre Absage wurde registriert.'}
        </Typography>
        {inviteForm?.frontendSettings?.declineText ? (
          <Grid container direction="row" justifyContent="center">
            <JsxParser
              components={{ Typography, Grid, Link: ExternalLink }}
              jsx={inviteForm.frontendSettings.declineText}
              renderInWrapper={false}
              autoCloseVoidElements
            />
          </Grid>
        ) : (
          <Typography sx={{ textAlign: 'center' }}>
            Wir würden uns freuen, Sie bei einer zukünftigen Veranstaltung begrüßen zu dürfen.
          </Typography>
        )}
        {/* <Grid container direction="row" justifyContent="center" sx={{ pt: 4 }}>
          <Button
            variant="outlined"
            to={`/einladung/${inviteCode}/response`}
            component={Link}
          >
            Zurück
          </Button>
        </Grid> */}
      </FormContainer>
    </Container>
  );
}
